import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { allocations } from "../../api/endpoints/allocations";
import { KNOWN_MARKET_POOLS } from "../../api/types/allocation";
import { qk } from "../../api/queryKeys";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { SelectField } from "../../components/forms/SelectField";

export function AllocateAccountInline({
  accountSlug, canWrite, gateOk, onAllocated,
}: {
  accountSlug: string; canWrite: boolean; gateOk: boolean; onAllocated: () => void;
}) {
  const qc = useQueryClient();
  const [marketPool, setMarketPool] = useState("");   // required; no inference from account_kind
  const preview = useQuery({
    enabled: Boolean(marketPool),
    queryKey: qk.allocationPreview("account", `${accountSlug}:${marketPool}`),
    queryFn: () =>
      allocations.preview({ kind: "account", market_pool: marketPool }).then((r) => r.data),
  });
  const commit = useMutation({
    mutationFn: () => allocations.allocateAccount({ market_pool: marketPool, account_slug: accountSlug }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.allocationLedger() });
      void qc.invalidateQueries({ queryKey: qk.allocationGate() });
      onAllocated();
    },
  });

  return (
    <div style={{ padding: 12, border: "1px solid var(--border)", borderRadius: 8 }}>
      <div className="label">ACCOUNT · {accountSlug}</div>
      <p>Status: <strong>not allocated</strong></p>
      <SelectField
        label="Market pool"
        value={marketPool}
        onChange={setMarketPool}
        options={[{ value: "", label: "Select a market pool…" }, ...KNOWN_MARKET_POOLS]}
      />
      {marketPool && preview.data && (
        <p>Next free block (policy): <strong>{preview.data.cidr ?? "—"}</strong></p>
      )}
      <button
        type="button"
        disabled={!canWrite || !gateOk || !marketPool || !preview.data?.cidr || commit.isPending}
        onClick={() => commit.mutate()}
      >
        {commit.isPending ? "Allocating…" : "Allocate account CIDR"}
      </button>
      {commit.error && <ErrorBanner error={commit.error} title="Allocation failed" />}
    </div>
  );
}
